import axios from 'axios';
import { resetPosthog } from '../analytics/posthog';

// API base URL resolution strategy:
//   - Production: REACT_APP_API_URL or domain defaults (never guess prod hosts here).
//   - Development on localhost / 127.0.0.1: use same-origin + src/setupProxy.js → :8000
//     unless REACT_APP_API_DIRECT=1 (then REACT_APP_API_URL or 127.0.0.1:8000).
//   - milan-ai.net → managed prod backend.
//
// Do NOT add new domain-based fallbacks — ship new envs via
// REACT_APP_API_URL instead. Having multiple hostname fallbacks here is how
// the previous "live-key leaked to staging" incident happened for Razorpay.
const getApiBaseUrl = () => {
  const explicit = (process.env.REACT_APP_API_URL || '').trim();
  const forceDirect =
    String(process.env.REACT_APP_API_DIRECT || '').trim() === '1' ||
    String(process.env.REACT_APP_API_DIRECT || '').toLowerCase() === 'true';

  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && !forceDirect) {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') {
      return '';
    }
  }

  if (explicit) {
    return explicit;
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'milan-ai.net' || hostname === 'www.milan-ai.net') {
      return 'https://milan-ai-prod.onrender.com';
    }
  }

  return 'http://127.0.0.1:8000';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Resolved API origin — useful for error messages when login fails with network errors */
export { API_BASE_URL };

function isLocalDevHostname() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

/**
 * Backend origin for user-facing errors. When using dev proxy, `API_BASE_URL` is '' but
 * FastAPI still runs on port 8000 — this string explains that for debugging.
 */
export const API_RESOLVED_FOR_ERRORS =
  API_BASE_URL ||
  (process.env.NODE_ENV === 'development' && isLocalDevHostname()
    ? 'http://127.0.0.1:8000 (via dev server proxy from :3000)'
    : 'http://127.0.0.1:8000');

api.interceptors.request.use((config) => {
  const path = config.url || '';
  // Never send a stale session token with credential exchange endpoints — avoids
  // edge cases with proxies/middleware and keeps login independent of old JWTs.
  const isAnonymousAuthCall =
    path.includes('/auth/login') ||
    path.includes('/auth/signup/') ||
    path.includes('/auth/google/auth') ||
    path.includes('/auth/forgot-password') ||
    path.includes('/auth/verify-reset-otp') ||
    path.includes('/auth/reset-password');

  if (isAnonymousAuthCall) {
    delete config.headers.Authorization;
    return config;
  }

  // Check for admin token first for admin routes
  if (path.includes('/admin')) {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
      return config;
    }
  }

  // Regular user token
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ----- Global 401 handler ---------------------------------------------------
//
// Why this exists:
//   With a 24h JWT expiry (production setting), a user who keeps a tab open
//   for >24h will start receiving 401s on every API call. Without this
//   interceptor each dashboard's `.catch()` blocks silently swallowed those
//   401s and showed mock data with a misleading "Unable to connect to
//   server" banner. From the user's POV the app looked broken — actually
//   their session had simply expired.
//
// Behaviour:
//   - On 401 from any endpoint, if the user *had* a token in localStorage,
//     clear the auth state and redirect to the appropriate login page,
//     preserving the page they were on so they land back there after re-login.
//   - Endpoints that *expect* a 401 in normal operation (login form, OTP
//     verification, password reset flow) are exempt — the caller already
//     handles those inline (e.g. "wrong password" message).
//   - A guard flag prevents N parallel failed requests from triggering N
//     navigations.
//
// Tokens are cleared via direct localStorage access (rather than the
// `logout()` helper exported below) because that helper is defined later
// in the file and `const` declarations aren't hoisted.

let _authRedirectInProgress = false;

const _AUTH_ENDPOINTS_ALLOW_401 = [
  '/auth/login',
  '/auth/google/auth',
  '/auth/admin/login',
  '/auth/admin/verify-otp',
  '/auth/admin/resend-otp',
  '/auth/admin/webauthn/register/begin',
  '/auth/admin/webauthn/register/complete',
  '/auth/admin/webauthn/authenticate/begin',
  '/auth/admin/webauthn/authenticate/complete',
  '/auth/forgot-password',
  '/auth/verify-reset-otp',
  '/auth/reset-password',
];

const _clearUserSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userName');
  localStorage.removeItem('userType');
  localStorage.removeItem('userId');
  localStorage.removeItem('instagramConnected');
  resetPosthog();
};

const _clearAdminSession = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminId');
  localStorage.removeItem('adminEmail');
  localStorage.removeItem('adminName');
  localStorage.removeItem('adminRole');
  resetPosthog();
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status !== 401) {
      return Promise.reject(error);
    }

    const url = error?.config?.url || '';
    if (_AUTH_ENDPOINTS_ALLOW_401.some((suffix) => url.includes(suffix))) {
      return Promise.reject(error);
    }

    // Decide which session expired based on which token slot is populated
    // for the URL being called. Admin routes use adminToken, everything
    // else uses the regular user token.
    const isAdminUrl = url.includes('/admin');
    const hadAdminToken = !!localStorage.getItem('adminToken');
    const hadUserToken = !!localStorage.getItem('token');

    if (typeof window !== 'undefined' && !_authRedirectInProgress) {
      const here = window.location.pathname + window.location.search;
      if (isAdminUrl && hadAdminToken) {
        _authRedirectInProgress = true;
        _clearAdminSession();
        window.location.href = `/admin/login?session_expired=1&redirect=${encodeURIComponent(here)}`;
      } else if (!isAdminUrl && hadUserToken) {
        _authRedirectInProgress = true;
        const userType = localStorage.getItem('userType') || 'brand';
        _clearUserSession();
        window.location.href = `/login?type=${userType}&session_expired=1&redirect=${encodeURIComponent(here)}`;
      }
    }

    return Promise.reject(error);
  }
);

export const healthCheck = () => api.get('/health_check');

export const getStats = () => api.get('/api/stats');

export const getAllCampaigns = () => api.get('/api/campaigns');

export const getActiveCampaigns = () => api.get('/api/campaigns/active');

export const getAllCreators = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append('search', params.search);
  if (params.platform) queryParams.append('platform', params.platform);
  if (params.niche) queryParams.append('niche', params.niche);
  if (params.city) queryParams.append('city', params.city);
  if (params.minFollowers) queryParams.append('min_followers', params.minFollowers);
  if (params.maxFollowers) queryParams.append('max_followers', params.maxFollowers);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.skip) queryParams.append('skip', params.skip);
  
  const queryString = queryParams.toString();
  return api.get(`/api/creators${queryString ? `?${queryString}` : ''}`);
};

export const getAllBrands = () => api.get('/api/brands');

export const login = (email, password) => 
  api.post('/auth/login', { email, password });

export const loginWithType = (email, password, userType) => 
  api.post('/auth/login', { email, password, user_type: userType });

export const signupBrand = (data) => 
  api.post('/auth/signup/brand', data);

export const signupCreator = (data) => 
  api.post('/auth/signup/creator', data);

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userName');
  localStorage.removeItem('userType');
  localStorage.removeItem('userId');
  resetPosthog();
};

export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

export const getCurrentUser = () => {
  return localStorage.getItem('userEmail');
};

export const getUserName = () => {
  return localStorage.getItem('userName');
};

export const getUserType = () => {
  return localStorage.getItem('userType');
};

export const getUserId = () => {
  return localStorage.getItem('userId');
};

// ============ GOOGLE OAUTH ============

export const googleAuth = (token, userType, additionalData = {}) =>
  api.post('/auth/google/auth', {
    token,
    user_type: userType,
    ...additionalData
  });

export const getGoogleClientId = () =>
  api.get('/auth/google/client-id');

// ============ BRANDS DASHBOARD API ============

export const getBrandDashboard = (brandId) => 
  api.get(`/api/brand-dashboard/brand/${brandId}/dashboard`);

export const getBrandCampaigns = (brandId, status = null) => {
  const params = status ? `?status=${status}` : '';
  return api.get(`/api/brand-dashboard/brand/${brandId}/campaigns${params}`);
};

export const createCampaignBlueprint = (data) => 
  api.post('/api/brand-dashboard/campaign/blueprint', data);

export const getCampaignRecommendations = (campaignId, limit = 20) => 
  api.get(`/api/brand-dashboard/campaign/${campaignId}/recommendations?limit=${limit}`);

export const selectOrRejectCreator = (data) => 
  api.post('/api/brand-dashboard/campaign/creator-selection', data);

export const submitCampaign = (campaignId) => 
  api.post(`/api/brand-dashboard/campaign/${campaignId}/submit`);

export const getCampaignAnalytics = (campaignId) => 
  api.get(`/api/brand-dashboard/campaign/${campaignId}/analytics`);

export const getCreatorsPerformance = (campaignId) => 
  api.get(`/api/brand-dashboard/campaign/${campaignId}/creators-performance`);

export const getSavedCreators = (brandId) => 
  api.get(`/api/brand-dashboard/crm/${brandId}/creators`);

export const removeCreatorFromCRM = (brandId, creatorId) => 
  api.delete(`/api/brand-dashboard/crm/${brandId}/creator/${creatorId}`);

export const getBrandFunds = (brandId) =>
  api.get(`/api/brand-dashboard/brand/${brandId}/funds`);

// Brand bank details (for refunds)
export const getBrandBankDetails = (brandId) =>
  api.get(`/api/brand-dashboard/brand/${brandId}/bank-details`);

export const saveBrandBankDetails = (brandId, details) =>
  api.post(`/api/brand-dashboard/brand/${brandId}/bank-details`, details);

export const deleteBrandBankDetails = (brandId) =>
  api.delete(`/api/brand-dashboard/brand/${brandId}/bank-details`);

// ============ CREATORS DASHBOARD API ============

export const getCreatorDashboard = (creatorId) => 
  api.get(`/api/creator-dashboard/creator/${creatorId}/dashboard`);

export const getCreatorCampaigns = (creatorId) => 
  api.get(`/api/creator-dashboard/creator/${creatorId}/campaigns`);

export const getCreatorTasks = (creatorId) => 
  api.get(`/api/creator-dashboard/creator/${creatorId}/tasks`);

export const updateTaskStatus = (data) => 
  api.patch('/api/creator-dashboard/creator/task/update', data);

export const submitContent = (data) => 
  api.post('/api/creator-dashboard/creator/submission', data);

export const getCreatorSubmissions = (creatorId) => 
  api.get(`/api/creator-dashboard/creator/${creatorId}/submissions`);

export const getCreatorInvoices = (creatorId) => 
  api.get(`/api/creator-dashboard/creator/${creatorId}/invoices`);

export const createInvoice = (data) => 
  api.post('/api/creator-dashboard/creator/invoice/create', data);

export const updateInvoiceStatus = (invoiceId, status) => 
  api.patch(`/api/creator-dashboard/creator/invoice/${invoiceId}/status?status=${status}`);

export const getCreatorAnalytics = (creatorId) => 
  api.get(`/api/creator-dashboard/creator/${creatorId}/analytics`);

export const getCreatorCampaignPerformance = (creatorId, campaignId) => 
  api.get(`/api/creator-dashboard/creator/${creatorId}/campaign/${campaignId}/performance`);

export const getCreatorEarnings = (creatorId) => 
  api.get(`/api/creator-dashboard/creator/${creatorId}/earnings`);

// ============ INSTAGRAM OAUTH API ============

export const getInstagramAuthUrl = (creatorId = null, redirectAfter = '/creator-dashboard') => 
  api.get(`/instagram/auth/url`, { 
    params: { 
      creator_id: creatorId, 
      redirect_after: redirectAfter 
    } 
  });

export const connectInstagram = (data) => 
  api.post('/instagram/connect', data);

export const disconnectInstagram = (creatorId) => 
  api.delete(`/instagram/disconnect/${creatorId}`);

export const getInstagramProfile = (creatorId) => 
  api.get(`/instagram/profile/${creatorId}`);

export const getInstagramMedia = (creatorId, limit = 10) => 
  api.get(`/instagram/media/${creatorId}?limit=${limit}`);

export const getInstagramInsights = (creatorId) => 
  api.get(`/instagram/insights/${creatorId}`);

export const getInstagramComments = (creatorId, limit = 5) => 
  api.get(`/instagram/comments/${creatorId}?limit=${limit}`);

export const verifyInstagramPost = (creatorId, postUrl) => 
  api.get('/instagram/verify-post', { params: { creator_id: creatorId, post_url: postUrl } });

export const getInstagramStatus = (creatorId) => 
  api.get(`/instagram/status/${creatorId}`);

export const refreshInstagramToken = (creatorId) => 
  api.post(`/instagram/refresh-token/${creatorId}`);

export const getInstagramTokenInfo = (creatorId) => 
  api.get(`/instagram/token-info/${creatorId}`);

// ============ CREATOR PREFERENCES API ============

export const updateCreatorPreferences = (creatorId, preferences) => 
  api.put(`/instagram/preferences/${creatorId}`, preferences);

export const getCreatorPreferences = (creatorId) => 
  api.get(`/instagram/preferences/${creatorId}`);

export const getCreatorRecommendationData = (creatorId) => 
  api.get(`/instagram/recommendation-data/${creatorId}`);

// ============ CAMPAIGN WORKFLOW API ============

export const simulateCampaign = (data) => 
  api.post('/api/campaigns/simulate', data);

export const createCampaignFromSimulation = (data) => 
  api.post('/api/campaigns/create-from-simulation', data);

export const selectCreatorForCampaign = (data) => 
  api.post('/api/campaigns/select-creator', data);

export const getCampaignAllocation = (campaignId) => 
  api.get(`/api/campaigns/allocation/${campaignId}`);

export const finalizeCampaign = (data) => 
  api.post('/api/campaigns/finalize', data);

export const deleteCampaign = (campaignId) => 
  api.delete(`/api/campaigns/${campaignId}`);

export const resendCampaignInvitations = (campaignId) => 
  api.post(`/api/campaigns/${campaignId}/resend-invitations`);

export const resendInvitationEmail = (campaignId, invitationId) =>
  api.post(`/api/campaigns/${campaignId}/invitations/${invitationId}/resend-email`);

export const getCampaignDetails = (campaignId) => 
  api.get(`/api/brand-dashboard/campaign/${campaignId}/details`);

export const getCreatorInvitations = (creatorId) => 
  api.get(`/api/campaigns/invitations/${creatorId}`);

export const respondToInvitation = (invitationId, action) => 
  api.post(`/api/campaigns/invitation/respond?invitation_id=${invitationId}&action=${action}`);

export const submitContentForReview = (data) => 
  api.post('/api/campaigns/content/submit', data);

export const verifyPublishedContent = (data) => 
  api.post('/api/campaigns/content/verify-url', data);

export const getCampaignWorkflowStatus = (campaignId) => 
  api.get(`/api/campaigns/status/${campaignId}`);

export const getCampaignSubmissions = (campaignId) => 
  api.get(`/api/brand-dashboard/campaign/${campaignId}/submissions`);

export const reviewSubmission = (data) => 
  api.post('/api/brand-dashboard/submission/review', data);

// Performance tracking APIs
export const getCreatorPerformance = (creatorId) =>
  api.get(`/api/creator-dashboard/creator/${creatorId}/performance`);

export const refreshCreatorPerformance = (creatorId, trackingId) =>
  api.post(`/api/creator-dashboard/creator/${creatorId}/performance/${trackingId}/refresh`);

export const getCampaignPerformance = (campaignId) =>
  api.get(`/api/brand-dashboard/campaign/${campaignId}/performance`);

export const refreshCampaignPerformance = (campaignId) =>
  api.post(`/api/brand-dashboard/campaign/${campaignId}/performance/refresh`);

// CRM APIs
export const getBrandCampaignCreators = (brandId) =>
  api.get(`/api/brand-dashboard/brand/${brandId}/campaign-creators`);

export const saveCreatorToCRM = (brandId, data) =>
  api.post(`/api/brand-dashboard/brand/${brandId}/crm/save-creator`, data);

// Email Verification APIs
export const resendVerificationEmail = (email) =>
  api.post('/auth/resend-verification', { email });

export const checkVerificationStatus = (email) =>
  api.get(`/auth/check-verification/${encodeURIComponent(email)}`);

// Contact Form API
export const sendContactForm = (data) =>
  api.post('/email/contact', data);

// Data Deletion API
export const requestDataDeletion = (data) =>
  api.post('/auth/data-deletion-request', {
    email: data.email,
    account_type: data.accountType,
    reason: data.reason
  });

// ============ PAYMENT API ============

export const createPaymentOrder = (data) =>
  api.post('/api/payments/create-order', data);

export const verifyPayment = (data) =>
  api.post('/api/payments/verify-payment', data);

// Recover an orphaned payment by polling Razorpay (used when the brand paid
// but the browser tab refreshed/closed before /verify-payment ran).
export const recoverPayment = (campaignId) =>
  api.post('/api/payments/recover', { campaign_id: campaignId });

export const getCampaignPaymentDetails = (campaignId) =>
  api.get(`/api/payments/campaign/${campaignId}`);

export const getCampaignTransactions = (campaignId) =>
  api.get(`/api/payments/transactions/${campaignId}`);

export const getCreatorPaymentEarnings = (creatorId) =>
  api.get(`/api/payments/creator-earnings/${creatorId}`);

// Creator Payment Details
export const getCreatorPaymentDetails = (creatorId) =>
  api.get(`/api/creators/${creatorId}/payment-details`);

export const saveCreatorPaymentDetails = (creatorId, details) =>
  api.post(`/api/creators/${creatorId}/payment-details`, details);

export const deleteCreatorPaymentDetails = (creatorId) =>
  api.delete(`/api/creators/${creatorId}/payment-details`);

// Razorpay public key ID.
//
// MUST be supplied via REACT_APP_RAZORPAY_KEY_ID at build time. We do NOT
// hardcode live/test keys by hostname anymore — that made it trivial for a
// stale preview deploy to charge real cards. If the env var is missing, we
// fall back to a known test key ONLY on localhost so local dev still works.
export const getRazorpayKeyId = () => {
  const envKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
  if (envKey) return envKey;

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'rzp_test_Sef8Dn8JHHVuYC';
    }
  }

  // Unconfigured in non-local env — fail loudly in the UI rather than
  // silently hitting a wrong/leaked key.
  console.error(
    '[Razorpay] REACT_APP_RAZORPAY_KEY_ID is not configured for this environment.'
  );
  return '';
};

// ============ ADMIN API ============

// Dashboard Overview
export const getAdminDashboardOverview = () =>
  api.get('/api/admin/dashboard/overview');

// User Analytics
export const getAdminUserAnalytics = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.user_type) queryParams.append('user_type', params.user_type);
  return api.get(`/api/admin/analytics/users?${queryParams.toString()}`);
};

// Campaign Analytics
export const getAdminCampaignAnalytics = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.status_filter) queryParams.append('status_filter', params.status_filter);
  return api.get(`/api/admin/analytics/campaigns?${queryParams.toString()}`);
};

// Invitation Analytics
export const getAdminInvitationAnalytics = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  return api.get(`/api/admin/analytics/invitations?${queryParams.toString()}`);
};

// Creators List
export const getAdminCreators = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.instagram_connected !== undefined) queryParams.append('instagram_connected', params.instagram_connected);
  if (params.has_payment_details !== undefined) queryParams.append('has_payment_details', params.has_payment_details);
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);
  if (params.sort_order) queryParams.append('sort_order', params.sort_order);
  return api.get(`/api/admin/creators?${queryParams.toString()}`);
};

// Brands List
export const getAdminBrands = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);
  if (params.sort_order) queryParams.append('sort_order', params.sort_order);
  return api.get(`/api/admin/brands?${queryParams.toString()}`);
};

// Communications - Rejected Creators
export const getAdminRejectedCreators = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  return api.get(`/api/admin/communications/rejected-creators?${queryParams.toString()}`);
};

// Payments
export const getAdminPendingPayments = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  return api.get(`/api/admin/payments/pending?${queryParams.toString()}`);
};

export const markCreatorPaid = (data) =>
  api.post('/api/admin/payments/mark-paid', data);

export const getAdminPaymentHistory = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  return api.get(`/api/admin/payments/history?${queryParams.toString()}`);
};

// Export URLs
export const getAdminExportUrl = (type) => {
  const baseUrl = API_BASE_URL;
  return `${baseUrl}/api/admin/export/${type}`;
};

// Database Explorer
export const getAdminDatabaseCollections = () =>
  api.get('/api/admin/database/collections');

export const browseAdminCollection = (collectionName, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.search_field) queryParams.append('search_field', params.search_field);
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);
  if (params.sort_order) queryParams.append('sort_order', params.sort_order);
  return api.get(`/api/admin/database/browse/${collectionName}?${queryParams.toString()}`);
};

export const getAdminDocument = (collectionName, documentId) =>
  api.get(`/api/admin/database/document/${collectionName}/${documentId}`);

// Revenue Dashboard
export const getAdminRevenueDashboard = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  return api.get(`/api/admin/revenue/dashboard?${queryParams.toString()}`);
};

// Campaign ROI
export const getAdminCampaignROI = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.campaign_id) queryParams.append('campaign_id', params.campaign_id);
  return api.get(`/api/admin/campaigns/roi?${queryParams.toString()}`);
};

// Content Rejections
export const getCampaignContentRejections = (campaignId) =>
  api.get(`/api/admin/campaigns/${campaignId}/rejections`);

export const getAllContentRejections = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.status_filter) queryParams.append('status_filter', params.status_filter);
  return api.get(`/api/admin/content-rejections?${queryParams.toString()}`);
};

// Platform Health
export const getAdminPlatformHealth = () =>
  api.get('/api/admin/platform/health');

// Alerts
export const getAdminAlerts = () =>
  api.get('/api/admin/alerts');

// Bulk Email
export const sendAdminBulkEmail = (data) =>
  api.post('/api/admin/bulk-email/send', data);

export const getAdminBulkEmailHistory = () =>
  api.get('/api/admin/bulk-email/history');

// Campaign Controls
export const adminCampaignControl = (campaignId, data) =>
  api.post(`/api/admin/campaigns/${campaignId}/control`, data);

// Refunds
// User Management
export const adminUserAction = (userType, userId, data) =>
  api.post(`/api/admin/users/${userType}/${userId}/action`, data);

export const getAdminSuspendedUsers = () =>
  api.get('/api/admin/users/suspended');

// Admin Authentication
export const adminLogin = (email, password) =>
  api.post('/auth/admin/login', { email, password });

export const adminVerifyOTP = (email, otp) =>
  api.post('/auth/admin/verify-otp', { email, otp });

export const adminResendOTP = (email) =>
  api.post(`/auth/admin/resend-otp?email=${encodeURIComponent(email)}`);

export const verifyAdminToken = () =>
  api.get('/auth/admin/verify');

// ============ ADMIN WEBAUTHN (passkey / Touch ID) ============
// Device-bound second factor. The backend returns `requires_passkey` from
// verify-otp with a short-lived `webauthn_ticket`; we run the browser
// ceremony here and exchange it for the real admin access token.

/** True if this browser/platform can do WebAuthn at all. */
export const isWebAuthnSupported = () =>
  typeof window !== 'undefined' &&
  !!window.PublicKeyCredential &&
  typeof navigator !== 'undefined' &&
  !!(navigator.credentials && navigator.credentials.create);

const _bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    str += String.fromCharCode(bytes[i]);
  }
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const _base64urlToBuffer = (base64url) => {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (padded.length % 4)) % 4;
  const binary = window.atob(padded + '='.repeat(padLen));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

/** Convert server creation options (base64url) into the binary shape the browser needs. */
const _toCreationOptions = (options) => ({
  ...options,
  challenge: _base64urlToBuffer(options.challenge),
  user: { ...options.user, id: _base64urlToBuffer(options.user.id) },
  excludeCredentials: (options.excludeCredentials || []).map((c) => ({
    ...c,
    id: _base64urlToBuffer(c.id),
  })),
});

/** Convert server request options (base64url) into the binary shape the browser needs. */
const _toRequestOptions = (options) => ({
  ...options,
  challenge: _base64urlToBuffer(options.challenge),
  allowCredentials: (options.allowCredentials || []).map((c) => ({
    ...c,
    id: _base64urlToBuffer(c.id),
  })),
});

const _serializeAttestation = (cred) => ({
  id: cred.id,
  rawId: _bufferToBase64url(cred.rawId),
  type: cred.type,
  response: {
    attestationObject: _bufferToBase64url(cred.response.attestationObject),
    clientDataJSON: _bufferToBase64url(cred.response.clientDataJSON),
    transports:
      typeof cred.response.getTransports === 'function' ? cred.response.getTransports() : [],
  },
  clientExtensionResults:
    typeof cred.getClientExtensionResults === 'function' ? cred.getClientExtensionResults() : {},
});

const _serializeAssertion = (cred) => ({
  id: cred.id,
  rawId: _bufferToBase64url(cred.rawId),
  type: cred.type,
  response: {
    authenticatorData: _bufferToBase64url(cred.response.authenticatorData),
    clientDataJSON: _bufferToBase64url(cred.response.clientDataJSON),
    signature: _bufferToBase64url(cred.response.signature),
    userHandle: cred.response.userHandle ? _bufferToBase64url(cred.response.userHandle) : null,
  },
  clientExtensionResults:
    typeof cred.getClientExtensionResults === 'function' ? cred.getClientExtensionResults() : {},
});

/**
 * One-time passkey enrolment for this device. Runs begin -> Touch ID -> complete.
 * Resolves with the admin-login response (incl. access_token) on success.
 */
export const adminPasskeyRegister = async (ticket, deviceLabel) => {
  const beginRes = await api.post('/auth/admin/webauthn/register/begin', { ticket });
  const publicKey = _toCreationOptions(beginRes.data);
  const credential = await navigator.credentials.create({ publicKey });
  if (!credential) throw new Error('Passkey creation was cancelled.');
  const completeRes = await api.post('/auth/admin/webauthn/register/complete', {
    ticket,
    credential: _serializeAttestation(credential),
    device_label: deviceLabel || 'My laptop',
  });
  return completeRes.data;
};

/**
 * Passkey login for an already-enrolled device. Runs begin -> Touch ID -> complete.
 * Resolves with the admin-login response (incl. access_token) on success.
 */
export const adminPasskeyAuthenticate = async (ticket) => {
  const beginRes = await api.post('/auth/admin/webauthn/authenticate/begin', { ticket });
  const publicKey = _toRequestOptions(beginRes.data);
  const assertion = await navigator.credentials.get({ publicKey });
  if (!assertion) throw new Error('Passkey verification was cancelled.');
  const completeRes = await api.post('/auth/admin/webauthn/authenticate/complete', {
    ticket,
    credential: _serializeAssertion(assertion),
  });
  return completeRes.data;
};

export const adminChangePassword = (currentPassword, newPassword) =>
  api.post('/auth/admin/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  });

export const isAdminLoggedIn = () => {
  return !!localStorage.getItem('adminToken');
};

export const adminLogout = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminId');
  localStorage.removeItem('adminEmail');
  localStorage.removeItem('adminName');
  localStorage.removeItem('adminRole');
  resetPosthog();
};

export const getAdminInfo = () => {
  return {
    adminId: localStorage.getItem('adminId'),
    email: localStorage.getItem('adminEmail'),
    name: localStorage.getItem('adminName'),
    role: localStorage.getItem('adminRole')
  };
};

// ==================== FORGOT PASSWORD APIs ====================

export const forgotPassword = (email, userType) =>
  api.post('/auth/forgot-password', { email, user_type: userType });

export const verifyResetOTP = (email, otp, userType) =>
  api.post('/auth/verify-reset-otp', { email, otp, user_type: userType });

export const resetPassword = (email, otp, newPassword, userType) =>
  api.post('/auth/reset-password', { 
    email, 
    otp, 
    new_password: newPassword, 
    user_type: userType 
  });

export const resendResetOTP = (email, userType) =>
  api.post('/auth/forgot-password', { email, user_type: userType });

// ==================== CAMPAIGN CANCELLATION APIs ====================

export const getCampaignCancelPreview = (campaignId) =>
  api.get(`/api/brand-dashboard/campaign/${campaignId}/cancel-preview`);

export const cancelCampaign = (campaignId, reason = '') =>
  api.post(`/api/brand-dashboard/campaign/${campaignId}/cancel`, { reason });

// Get brand's refund history and status
export const getBrandRefunds = () =>
  api.get('/api/brand-dashboard/refunds');

// ==================== INDIVIDUAL CREATOR PAYMENT APIs ====================

// Get payment schedule with individual creator timelines
export const getCampaignPaymentSchedule = (campaignId) =>
  api.get(`/api/brand-dashboard/campaign/${campaignId}/payment-schedule`);

// Get monitoring overview for all creators in a campaign
export const getCampaignMonitoringOverview = (campaignId) =>
  api.get(`/api/brand-dashboard/campaign/${campaignId}/monitoring-overview`);

// Get individual creator timeline and payment status
export const getCreatorTimeline = (campaignId, creatorId) =>
  api.get(`/api/brand-dashboard/campaign/${campaignId}/creator/${creatorId}/timeline`);

// Get escrow summary for a campaign (shows escrow breakdown, payments, refunds)
export const getCampaignEscrowSummary = (campaignId) =>
  api.get(`/api/brand-dashboard/campaign/${campaignId}/escrow-summary`);

// NOTE: Brand-facing payment release endpoints were removed. Creator payouts
// are now made offline by the Milan AI team and recorded via the admin
// dashboard (see getAdminPendingPayments / markCreatorPaid below).

// ==================== ADMIN REFUND MANAGEMENT APIs ====================

// Get pending refunds for admin
export const getAdminPendingRefunds = (period = 'all') =>
  api.get('/api/admin/refunds/pending', { params: { period } });

// Process a refund (process via Razorpay, mark complete, or mark failed)
export const processAdminRefund = (data) =>
  api.post('/api/admin/refunds/process', {
    refund_id: data.refund_id,
    campaign_id: data.campaign_id,
    action: data.action || 'process',  // "process", "complete", "fail", "retry"
    amount: data.amount,
    utr_reference: data.utr_reference,
    reason: data.reason,
    notes: data.notes
  });

// Get refund history
export const getAdminRefundHistory = (period = 'all', limit = 50, offset = 0) =>
  api.get('/api/admin/refunds/history', { params: { period, limit, offset } });

// Best-effort live Razorpay merchant balance (advisory; does not block UI)
export const getRazorpayBalance = () => api.get('/api/admin/razorpay/balance');

// ==================== PERFORMANCE HUB (Instagram post tracking) ====================

export const perfHubGetEntitlement = () => api.get('/api/performance-hub/entitlement');
export const perfHubGetBilling = () => api.get('/api/performance-hub/billing');
export const perfHubDevUpgradePaid = (note) =>
  api.post('/api/performance-hub/billing/dev-upgrade-to-paid', { note: note || '' });
export const perfHubListCampaigns = (params) =>
  api.get('/api/performance-hub/campaigns', { params: params || {} });
export const perfHubCreateCampaign = (name, monitoringOpts = 4) => {
  const payload = { name: name || 'Instagram tracking' };
  if (typeof monitoringOpts === 'number') {
    payload.monitoring_unit = 'weeks';
    payload.monitoring_weeks = monitoringOpts;
  } else if (monitoringOpts && typeof monitoringOpts === 'object') {
    const unit = monitoringOpts.monitoring_unit === 'days' ? 'days' : 'weeks';
    payload.monitoring_unit = unit;
    if (unit === 'days') {
      const d =
        monitoringOpts.monitoring_days != null
          ? Number(monitoringOpts.monitoring_days)
          : 14;
      payload.monitoring_days = Number.isFinite(d) ? d : 14;
    } else {
      const w =
        monitoringOpts.monitoring_weeks != null
          ? Number(monitoringOpts.monitoring_weeks)
          : 4;
      payload.monitoring_weeks = Number.isFinite(w) ? w : 4;
    }
  } else {
    payload.monitoring_unit = 'weeks';
    payload.monitoring_weeks = 4;
  }
  return api.post('/api/performance-hub/campaigns', payload);
};
export const perfHubPatchCampaign = (campaignId, body) =>
  api.patch(`/api/performance-hub/campaigns/${campaignId}`, body);
export const perfHubPatchEconomics = (campaignId, body) =>
  api.patch(`/api/performance-hub/campaigns/${campaignId}/economics`, body);
export const perfHubPatchPostGuardBrief = (campaignId, body) =>
  api.patch(`/api/performance-hub/campaigns/${campaignId}/post-guard`, body);
export const perfHubPatchRowPostGuard = (rowId, body) =>
  api.patch(`/api/performance-hub/rows/${rowId}/post-guard`, body);
export const perfHubCancelCampaign = (campaignId) =>
  api.post(`/api/performance-hub/campaigns/${campaignId}/cancel`);
export const perfHubCompleteCampaign = (campaignId) =>
  api.post(`/api/performance-hub/campaigns/${campaignId}/complete`);
export const perfHubAddCreatorRow = (campaignId, row) =>
  api.post(`/api/performance-hub/campaigns/${campaignId}/row`, {
    instagram_handle: row.instagram_handle,
    email: row.email,
    post_url: row.post_url,
  });
export const perfHubUploadCsv = (campaignId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/api/performance-hub/campaigns/${campaignId}/csv`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const perfHubStartMonitoring = (campaignId) =>
  api.post(`/api/performance-hub/campaigns/${campaignId}/start-monitoring`);
export const perfHubArchiveCampaign = (campaignId) =>
  api.post(`/api/performance-hub/campaigns/${campaignId}/archive`);
export const perfHubUnarchiveCampaign = (campaignId) =>
  api.post(`/api/performance-hub/campaigns/${campaignId}/unarchive`);
export const perfHubDeleteCampaign = (campaignId) =>
  api.delete(`/api/performance-hub/campaigns/${campaignId}`);
export const perfHubListRows = (campaignId) =>
  api.get(`/api/performance-hub/campaigns/${campaignId}/rows`);
export const perfHubDashboard = (campaignId, auto) =>
  api.get(`/api/performance-hub/campaigns/${campaignId}/dashboard`, {
    params: auto ? { auto: true } : {},
  });
export const perfHubRefresh = (campaignId) =>
  api.post(`/api/performance-hub/campaigns/${campaignId}/refresh`);
export const perfHubRefreshComments = (campaignId) =>
  api.post(`/api/performance-hub/campaigns/${campaignId}/refresh-comments`);
export const perfHubResendRow = (rowId) =>
  api.post(`/api/performance-hub/rows/${rowId}/resend`);
export const perfHubExportCsv = (campaignId) =>
  api.get(`/api/performance-hub/campaigns/${campaignId}/export`, { responseType: 'blob' });
export const perfHubCrmList = () => api.get('/api/performance-hub/crm');
export const perfHubCrmSave = (body) => api.post('/api/performance-hub/crm', body);
export const perfHubCrmDelete = (crmId) => api.delete(`/api/performance-hub/crm/${crmId}`);

export const perfHubCheckoutCreateOrder = (body) =>
  api.post('/api/performance-hub/checkout/create-order', body);
export const perfHubCheckoutVerify = (body) =>
  api.post('/api/performance-hub/checkout/verify', body);
export const perfHubCheckoutListPending = () =>
  api.get('/api/performance-hub/checkout/pending');
export const perfHubCheckoutRecover = (razorpayOrderId) =>
  api.post(
    `/api/performance-hub/checkout/recover/${encodeURIComponent(razorpayOrderId)}`
  );
export const perfHubCheckoutDismissUnpaid = (razorpayOrderId) =>
  api.post(
    `/api/performance-hub/checkout/dismiss-unpaid/${encodeURIComponent(razorpayOrderId)}`
  );

/** Public — no JWT; uses raw axios */
export const perfHubPublicInvite = (t) =>
  axios.get(`${API_BASE_URL}/api/performance-hub/public/invite`, { params: { t } });
export const perfHubPublicOAuthUrl = (t) =>
  axios.get(`${API_BASE_URL}/api/performance-hub/public/oauth-url`, { params: { t } });
export const perfHubPublicCatalogPrices = () =>
  axios.get(`${API_BASE_URL}/api/performance-hub/public/catalog-prices`);

// ==================== ADMIN — PERFORMANCE HUB ANALYTICS (read-only) ====================
// These hit a NEW backend module (performance_hub/admin_routes.py). They are
// gated by the same admin JWT as the rest of the admin dashboard — the
// request interceptor at the top of this file already sends `adminToken`
// for any URL containing "/admin".

/** KPI cards + time-bucketed trend strip for the admin Performance Hub section. */
export const perfHubAdminOverview = (params = {}) =>
  api.get('/api/admin/performance-hub/overview', { params });

/** Paginated PH campaigns list with brand + creator-funnel context. */
export const perfHubAdminCampaigns = (params = {}) =>
  api.get('/api/admin/performance-hub/campaigns', { params });

/** Full detail for one PH campaign — brand, all rows, latest-snapshot totals. */
export const perfHubAdminCampaignDetail = (campaignId) =>
  api.get(`/api/admin/performance-hub/campaigns/${campaignId}/detail`);

/** Worklist of invited creators who have NOT connected Instagram yet. */
export const perfHubAdminUnconnected = (params = {}) =>
  api.get('/api/admin/performance-hub/creators/unconnected', { params });

/** Per-brand PH plan + spend, filterable by plan tier. */
export const perfHubAdminBrands = (params = {}) =>
  api.get('/api/admin/performance-hub/brands', { params });

/** Revenue time-series for the selected period + granularity. */
export const perfHubAdminRevenue = (params = {}) =>
  api.get('/api/admin/performance-hub/revenue', { params });

/** Non-completed PH checkout orders (sales follow-up). */
export const perfHubAdminAbandonedCheckouts = (params = {}) =>
  api.get('/api/admin/performance-hub/checkout/abandoned', { params });

/** CSV export for PH admin (`kind`: overview | campaigns | unconnected | brands | abandoned_checkouts). */
export const perfHubAdminExport = (kind, params = {}) =>
  api.get(`/api/admin/performance-hub/export/${kind}`, { params, responseType: 'blob' });

export default api;
